/**
 * Canonical TTS text sanitization.
 *
 * This is the single source of truth for what markdown/HTML/citation/emoji
 * cleanup happens before text is spoken aloud. `backend/app/services/tts_text.py`
 * is a hand-maintained Python port of these same rules (literal code sharing
 * is impossible across languages), and `shared/tts-sanitizer-cases.json` is a
 * corpus run against `sanitizeForTTS` by BOTH implementations so they cannot
 * silently drift apart. If you change a rule here, update the corpus and the
 * Python port too. `sanitizeForTTSChunk` below shares the identical rule
 * chain and differs from `sanitizeForTTS` ONLY by the final `.trim()`.
 *
 * Order matters: more specific patterns are matched first (citations before
 * generic bracket cleanup, block markers before inline emphasis, bold before
 * italic, whitespace normalization last).
 *
 * Known accepted divergences (measured directly against both real
 * implementations — the corpus can only pin agreement, not disagreement, so
 * this list is the record. Line-terminator normalization is still open; the
 * BOM divergence was closed by the Unicode look-alike rule below, which
 * strips U+FEFF in both languages before anything else runs). Non-printing
 * codepoints below are named, not embedded literally, to keep this comment
 * plain-ASCII:
 *   input                                | JS            | Python
 *   --------------------------------------|---------------|------------------
 *   '- item' CR '- two'                   | 'item two'    | 'item, two'
 *   '1. one' CR '2. two'                  | 'one two'     | 'one 2. two'
 *   'a' LS '---' LS 'b'                   | 'a b'         | 'a, b'
 *   'x' NEL 'y'                           | unchanged (3) | 'x y'
 *   'x' US(unit sep, U+001F) 'y'          | unchanged (3) | 'x y'
 *   '[' + long-s(U+017F) + 'ource 1]'     | kept          | stripped (as citation)
 * Root causes: (a) JS `^`/`$` under `/m` treat CR, LS (U+2028), PS (U+2029) as
 * line terminators; Python's `re.MULTILINE` recognizes only `\n` (row 3: JS's
 * horizontal-rule rule sees "---" as its own line and deletes it outright;
 * Python's dash rule turns it into a comma instead). (b) JS `\s`/
 * `String.trim()` and Python `\s`/`str.strip()` disagree at the edges: JS
 * includes BOM (U+FEFF) but not NEL/the C0 separators (U+0085, U+001C-U+001F);
 * Python is the mirror image. (c) Python's `re.IGNORECASE` does Unicode case
 * folding (long-s U+017F folds to 's'); JS's `/i` without `/u` does not.
 * CRLF is safe: `"a\r\n---\r\nb"` sanitizes identically in both ('a b') —
 * the `\n` still carries the anchor, so don't re-investigate that case.
 */

/**
 * The full rule chain, untrimmed. Internal — callers use sanitizeForTTS
 * (whole-text, trimmed) or sanitizeForTTSChunk (per-chunk, untrimmed).
 */
function applyRules(text: string): string {
  return (
    text
      // === Unicode look-alike normalization (must be first) ===
      // RAG-quoted document text carries typographic look-alikes that break
      // byte-exact pronunciation-dictionary matching at the TTS provider
      // ("OM-B" written with U+2011 never matches grapheme "OM-B"). Hyphen
      // look-alikes fold to ASCII; invisible hints/joiners strip entirely.
      // U+FEFF must strip BEFORE whitespace collapse: it is \s in JS but
      // not in Python, so leaving it to the collapse both breaks words
      // ("FL" + FEFF + "250" would become "FL 250") and diverges from the
      // Python port.
      // En/em dashes are deliberately NOT folded — the dash rule below
      // treats them as clause markers.
      // U+2010 hyphen, U+2011 non-breaking hyphen, U+2012 figure dash, U+2212 minus
      .replace(/[\u2010\u2011\u2012\u2212]/g, '-')
      // U+00AD soft hyphen, U+200B-D zero-widths, U+2060 word joiner, U+FEFF BOM/ZWNBSP.
      // Standalone-codepoint class \u2014 same reasoning as the emoji rule's disable below.
      // eslint-disable-next-line no-misleading-character-class
      .replace(/[\u00AD\u200B\u200C\u200D\u2060\uFEFF]/g, '')

      // === Source citation removal ===
      // All [Source ...] formats: [Source 1], [Source one], [Source 1, Source 2], [Sources: 1, 2], etc.
      .replace(/\[Sources?\s*:?\s*[^\]]+\]/gi, '')
      // [N] style references
      .replace(/\[[\d,\s]+\]/g, '')
      // (Source N) or (Source one) parenthetical references — all formats
      .replace(/\(Sources?\s*:?\s*[^)]+\)/gi, '')
      // Standalone source markers before punctuation
      .replace(/\s*\[\d+\]\s*([.!?])/g, '$1')

      // === Code handling ===
      // Code blocks: remove entirely (empty string, not ". " — a trailing
      // ". " would strand orphan punctuation at the start of an utterance).
      .replace(/```[\s\S]*?```/g, '')
      // Inline code: keep the text content, just remove backtick wrappers
      .replace(/`([^`]+)`/g, '$1')

      // === URL and link handling ===
      // Markdown links: [text](url) → keep link text only
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Bare URLs (sound terrible when spoken)
      .replace(/https?:\/\/[^\s)]+/g, '')

      // === Markdown block elements ===
      // Horizontal rules (---, ***, ___ on their own line)
      .replace(/^[-*_]{3,}\s*$/gm, '')
      // Heading markers (## Title → Title, or standalone ### without trailing text)
      .replace(/^#{1,6}\s*/gm, '')
      // Hash sequences mid-text (2+ hashes not at line start, e.g. " ### ")
      .replace(/\s#{2,}\s/g, ' ')
      // Blockquote markers (> text → text)
      .replace(/^>\s*/gm, '')
      // Unordered list bullets (- item, * item, + item)
      .replace(/^[\s]*[-*+]\s+/gm, '')
      // Ordered list numbers (1. item → item)
      .replace(/^[\s]*\d+\.\s+/gm, '')

      // === Table syntax ===
      // Table separator rows (|---|---|).
      // Char class is intentionally ordered \s first so Tailwind's content
      // scanner does not misread it as an arbitrary-property class.
      .replace(/^\|[\s|:-]+\|$/gm, '')
      // Pipe characters → comma separation
      .replace(/\|/g, ', ')

      // === Dash/hyphen handling for TTS ===
      // Em dash (—) and en dash (–) → comma for natural pause
      .replace(/\s*[—–]\s*/g, ', ')
      // Double+ hyphens used as informal dash (--) → comma for natural pause
      .replace(/\s*--+\s*/g, ', ')
      // Standalone hyphen between words (e.g. "word - word") → comma
      // Won't match compound words (state-of-the-art) since those have no spaces
      .replace(/(\w)\s+-\s+(\w)/g, '$1, $2')

      // === Markdown inline formatting ===
      // Bold: **text** or __text__ → text
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/(?<!\w)__([^_]+)__(?!\w)/g, '$1')
      // Italic: *text* or _text_ → text (matched after bold to avoid conflicts)
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '$1')
      // Underscore emphasis is word-boundary guarded (not just underscore-adjacency
      // guarded) so it never mangles snake_case identifiers like
      // voice_response_enabled or persona_id, where the underscores sit inside a word.
      .replace(/(?<!\w)_([^_]+)_(?!\w)/g, '$1')
      // Strikethrough: ~~text~~ → text
      .replace(/~~([^~]+)~~/g, '$1')

      // === HTML handling ===
      // Common HTML entities → spoken equivalents
      .replace(/&amp;/g, ' and ')
      .replace(/&lt;/g, ' less than ')
      .replace(/&gt;/g, ' greater than ')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      // Strip any remaining HTML tags
      .replace(/<[^>]+>/g, '')

      // === Emoji removal ===
      // Common emoji ranges that TTS can't pronounce.
      // The /v flag would be cleaner for combining-char ranges, but it
      // requires ES2024 targets; the existing /u + range list works at
      // runtime for the standalone-codepoint cases we care about.
      .replace(
        // eslint-disable-next-line no-misleading-character-class
        /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}]/gu,
        ''
      )

      // === Cleanup after pattern removal ===
      // Remaining asterisks/underscores not caught by paired markdown patterns
      // (e.g., unmatched ** or * from partial markdown in streamed tokens)
      .replace(/\*+/g, '')
      .replace(/(?<!\w)_+|_+(?!\w)/g, '')
      // Empty containers left after other cleaning
      .replace(/\(\s*\)/g, '')
      .replace(/\[\s*\]/g, '')

      // === Whitespace normalization (must be last) ===
      // Multiple newlines → single space
      .replace(/\n+/g, ' ')
      // Multiple spaces → single space
      .replace(/\s+/g, ' ')
      // Space before punctuation
      .replace(/\s+([.!?,;:])/g, '$1')
  )
}

/**
 * Sanitize LLM-produced text for text-to-speech. Pure and total: never
 * throws, and returns '' for empty/nullish input.
 *
 * Idempotent for text that has already been trimmed / has no leading
 * whitespace before a block marker — NOT idempotent in general. A
 * differential fuzz found inputs like leading-whitespace + '>'/'#'/list
 * marker (e.g. '\n  \t>R&D#:C#') where a second pass strips content the
 * first pass left behind, because the line-anchored block rules only fire
 * at column 0 and the first pass's whitespace collapse can move a marker
 * there. Nothing in this codebase double-sanitizes today; do not rely on
 * full idempotence.
 *
 * Only use this on text that will be SPOKEN, never on text that is DISPLAYED
 * — it is lossy (citations, markdown, HTML all disappear). Use this for
 * whole, complete text (e.g. a full non-streamed reply). For fragments that
 * will be concatenated with neighbouring fragments, use sanitizeForTTSChunk
 * instead — trimming here would eat the fragment boundary.
 */
export function sanitizeForTTS(text: string): string {
  if (!text) return ''
  return applyRules(text).trim()
}

/**
 * Sanitize ONE CHUNK of a streamed TTS reply. Identical rule chain to
 * sanitizeForTTS, but deliberately does NOT trim.
 *
 * Why this must not trim: StreamingSanitizer.sanitize() runs once per
 * incoming stream token/chunk, and StreamingTextProcessor concatenates the
 * results with `phraseBuffer += cleaned`. A chunk that is purely whitespace
 * (e.g. the lone " " between "Hello" and "world" in a token stream) has to
 * sanitize to " ", not "" — otherwise the word boundary is lost and adjacent
 * chunks glue together ("Helloworld"). sanitizeForTTS's trim is safe only
 * because it runs once, on the complete text; applying it per-chunk is a
 * production bug (verified: it silently mangled the real ElevenLabs/local-TTS
 * streaming path). Do not "simplify" this back into one function — the two
 * behaviours are both required by different callers.
 *
 * The corpus (shared/tts-sanitizer-cases.json) pins sanitizeForTTS, not this
 * function — whole-text output is the contract Task 2's Python port matches.
 */
export function sanitizeForTTSChunk(text: string): string {
  if (!text) return ''
  return applyRules(text)
}
